import { createContext, useContext } from 'react';
import { Moon, Sun } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import Button from '../ui/Button';
import { RailShell } from './AppShell';
import { usePageTier } from './portalTier';
import './shell.css';

// The Review set shell (DESIGN rev 2.1 §2.6, §3.3): the icon rail for a logged-in viewer and nothing else for an
// anonymous share link. Every review page (HD-8: /review/:id, /share/:token; HD-9: /share/script/:token,
// /share/footage/:token) draws the whole review frame, BreadcrumbBar included; the shell owns the review tier: dark by
// default (light for the script share, `tier`), the viewer's choice kept in localStorage (per user when signed in, per
// page kind with `tierKey`). The tier is set on <html> while the page is open, so dialogs and toasts follow it.
const TierCtx = createContext({ tier: 'dark', toggle: null });
export const useReviewTier = () => useContext(TierCtx);

// The review tier toggle, for the page's breadcrumb bar (the same place for staff, client accounts and guests).
export function TierToggle() {
  const { tier, toggle } = useReviewTier();
  if (!toggle) return null;
  return <Button variant="quiet" iconOnly icon={tier === 'dark' ? Sun : Moon} label={tier === 'dark' ? 'Light theme' : 'Dark theme'} onClick={toggle} />;
}

// tier: the frame's default tier ('dark', or 'light' for a page with no monitor, the script share); tierKey: keeps that page's
// choice apart from the video review's (HD-9).
export default function ReviewShell({ tier, tierKey, children }) {
  return <TierShell tier={tier} tierKey={tierKey}>{children}</TierShell>;
}

function TierShell({ tier: fallback = 'dark', tierKey, children }) {
  const { user, logout } = useAuth();
  const scope = tierKey ? `hf-review-tier:${tierKey}` : 'hf-review-tier';
  const { tier, toggle } = usePageTier({ key: user ? `${scope}:${user.id}` : scope, fallback });
  const frame = (
    <TierCtx.Provider value={{ tier, toggle }}>
      <div className="ui-review-shell ui-review-shell--tier"><div className="ui-review-shell__page">{children}</div></div>
    </TierCtx.Provider>
  );
  if (!user) return frame;
  return <RailShell user={user} logout={logout} forceRail themeless>{frame}</RailShell>;
}
